import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Star, FileText, Download, ExternalLink } from 'lucide-react';
import type { JobCv } from '../types';
import { fetchCvs, uploadCv, setDefaultCv, deleteCv } from '../lib/jobCvApi';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const JobCvManager: React.FC = () => {
  const [cvs, setCvs] = useState<JobCv[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [cvName, setCvName] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchCvs();
      setCvs(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !cvName.trim()) return;

    try {
      setUploading(true);
      setError(null);
      await uploadCv(file, cvName.trim());
      setCvName('');
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultCv(id);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDelete = async (cv: JobCv) => {
    if (!window.confirm(`Delete "${cv.name}"?`)) return;
    try {
      await deleteCv(cv.id, cv.file_path);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-3xl font-bold text-theme-primary mb-2">CV Manager</h1>
        <p className="text-theme-secondary mb-6">Upload and manage your CVs</p>

        {error && (
          <div className="bg-red-500/10 text-red-400 rounded-lg p-3 text-sm mb-4">{error}</div>
        )}

        {/* Upload form */}
        <div className="bg-theme-primary rounded-lg border border-theme-primary p-4 mb-6">
          <h3 className="text-sm font-semibold text-theme-primary mb-3">Upload New CV</h3>
          <div className="space-y-3">
            <input
              type="text"
              value={cvName}
              onChange={(e) => setCvName(e.target.value)}
              placeholder="CV name (e.g. Frontend Developer CV)"
              className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="w-full text-sm text-theme-secondary file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
            />
            <button
              onClick={handleUpload}
              disabled={uploading || !cvName.trim()}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
            >
              <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload CV'}
            </button>
          </div>
        </div>

        {/* CV list */}
        {loading ? (
          <p className="text-center text-theme-secondary py-12">Loading...</p>
        ) : cvs.length === 0 ? (
          <p className="text-center text-theme-secondary py-12">No CVs uploaded yet.</p>
        ) : (
          <div className="space-y-2">
            {cvs.map((cv) => (
              <div key={cv.id} className="bg-theme-primary rounded-lg border border-theme-primary p-4 flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-theme-primary truncate">{cv.name}</span>
                    {cv.is_default && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">
                        Default
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-theme-secondary">{formatDate(cv.created_at)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {cv.url && (
                    <a href={cv.url} target="_blank" rel="noreferrer"
                      className="p-2 text-theme-secondary hover:text-blue-400 transition-colors" title="Open">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {cv.url && (
                    <a href={cv.url} download
                      className="p-2 text-theme-secondary hover:text-green-400 transition-colors" title="Download">
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                  {!cv.is_default && (
                    <button onClick={() => handleSetDefault(cv.id)}
                      className="p-2 text-theme-secondary hover:text-yellow-400 transition-colors" title="Set as default">
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleDelete(cv)}
                    className="p-2 text-theme-secondary hover:text-red-400 transition-colors" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobCvManager;
